import { Box, CircularProgress } from "@mui/material";
import { Fragment, useCallback, useMemo } from "react";
import { CoverageTierEnum, RateDesignEnum } from "@platform/openapi-types";

import { useGetRateScheduleStats } from "../../../hooks/useGetRateScheduleStats";
import { useIsVolumeBased } from "../../../hooks/useIsVolumeBased";
import { useUpdateRate, useUpdateProposalRate } from "../../../hooks/useRates";
import { isCriticalIllnessCoverageType } from "../../../utils/rate-helpers";
import {
  formatAgeBandLabel,
  formatRateValue,
  getEnrolledLabel,
  sortByAgeBandBounds,
} from "../../../utils/rateFormatters";
import {
  buildRateFieldPath,
  quantityFieldOf,
} from "../../../utils/rateFieldPath";
import { parseIntBaseTen } from "../../../utils/transforms";

import { CompanionRatesSection } from "./CompanionRatesSection";
import { DebouncedNumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import {
  RenewalGroupHeaderCell,
  RenewalRowCells,
  RenewalSubHeaderCells,
} from "./RenewalRateCell";
import {
  resolveDestinationPlans,
  useAgeBandedRateCopy,
} from "./useAgeBandedRateCopy";
import {
  AG_BORDER,
  AG_HEADER_BG_COLOR,
  FONT_SIZE,
  gridCellSx,
  headerCellSx,
  inputSx,
} from "./constants";

import type { RateCopyColumn } from "./useAgeBandedRateCopy";
import type { ColDef } from "ag-grid-community";
import type { AxiosInstance } from "axios";
import type { FC } from "react";
import type { Rate } from "../../../types";
import type { BenefitPlan, BenefitPlanGroup } from "../../../types/models";

export interface AgeBandedTieredRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  /** Coverage type (e.g., dental, vision, std) for LOC-specific behavior */
  coverageType?: string;
  onRateSaved: () => void;
  readOnly?: boolean;
  benefitPlanGroup?: BenefitPlanGroup;
  /** All plans in the coverage offered as copy destinations. */
  copyDestinationPlans?: BenefitPlan[];
}

/**
 * Build column definitions for age banded tiered rate design in RatesGrid
 */
export const buildAgeBandedTieredColumns = (
  _group: BenefitPlanGroup,
): ColDef[] =>
  // TODO: Extract unique age bands from rate schedules
  [];

/**
 * Build column definitions for age banded tiered rate design in SummaryGrid
 */
export const buildAgeBandedTieredSummaryColumns = (
  _planId: string,
  _planName: string,
): ColDef[] =>
  // TODO: Implement age banded tiered summary column generation
  [];

/**
 * Maps a coverage tier code to a human-readable label.
 */
const coverageTierToLabel = (
  tierCode?: CoverageTierEnum | string,
  tierCount?: number,
): string => {
  if (!tierCode) return "Unknown";

  switch (tierCode) {
    case CoverageTierEnum.employee:
      return "EE";
    case CoverageTierEnum.employeeSpouse:
      return "ES";
    case CoverageTierEnum.employeeChildren:
      return "EC";
    case CoverageTierEnum.employeeFamily:
      return "EF";
    case CoverageTierEnum.employeeDependent:
      return tierCount === 3 ? "ED" : "EC";
    case CoverageTierEnum.employee2Dependents:
      return tierCount === 3 ? "E2" : "E2C";
    case CoverageTierEnum.spouseOnly:
      return "SP";
    case CoverageTierEnum.spouseDependent:
      return "SD";
    case CoverageTierEnum.spouseChildren:
      return "SC";
    case CoverageTierEnum.childOnly:
      return "CH";
    default:
      return String(tierCode);
  }
};

/**
 * Grouped age band with rates for each coverage tier
 */
interface AgeBandGroup {
  lower?: number;
  upper?: number;
  ratesByTier: Map<string, Rate>;
}

/**
 * Renderer for age banded tiered rate design
 *
 * Displays rates by age band with separate columns for each coverage tier.
 * Each age band has its own row with numberOfLives and rate inputs for each tier.
 *
 * Layout:
 * | Age Banded Tiered (X bands)                                              [Save] |
 * | Age Band |    Employee      |   EE & Spouse    |   Full Family    | ... |
 * |          | Enrolled | Rate  | Enrolled | Rate  | Enrolled | Rate  | ... |
 * | 0-17     | [input]  |[input]| [input]  |[input]| [input]  |[input]| ... |
 * | 18-24    | [input]  |[input]| [input]  |[input]| [input]  |[input]| ... |
 * ...
 */
export const AgeBandedTieredRenderer: FC<AgeBandedTieredRendererProps> = ({
  plan,
  axiosInstance,
  coverageType,
  onRateSaved,
  readOnly,
  benefitPlanGroup,
  copyDestinationPlans,
}) => {
  const {
    rateSchedule,
    companionRateSchedule,
    rates,
    companionRates,
    renewal,
    isLoading,
  } = useGetRateScheduleStats(plan);
  const showRenewal = renewal.showRenewal;

  const isCriticalIllness = isCriticalIllnessCoverageType(coverageType);

  // Extract unique tiers from the rates (preserving order they appear)
  const uniqueTiers: string[] = useMemo(() => {
    const tierSet = new Set<string>();
    rates.forEach((rate) => {
      if (rate.coverageTierCode) {
        tierSet.add(rate.coverageTierCode);
      }
    });
    return Array.from(tierSet);
  }, [rates]);

  // Group rates by age band with a map of tier -> rate
  const ageBandGroups: AgeBandGroup[] = useMemo(() => {
    const groupMap = new Map<string, AgeBandGroup>();

    rates.forEach((rate) => {
      const key = `${rate.ageBandLower ?? "null"}-${rate.ageBandUpper ?? "null"}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          lower: rate.ageBandLower,
          upper: rate.ageBandUpper,
          ratesByTier: new Map(),
        });
      }

      const group = groupMap.get(key)!;
      if (rate.coverageTierCode) {
        group.ratesByTier.set(rate.coverageTierCode, rate);
      }
    });

    // Sort by lower age band
    return sortByAgeBandBounds(Array.from(groupMap.values()));
  }, [rates]);

  // Mutation hook for updating rates
  const updateRateMutation = useUpdateRate(axiosInstance!);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance!);
  const rateMutation =
    plan.rateScheduleSource === "proposal"
      ? updateProposalRateMutation
      : updateRateMutation;

  // Determine if rates use volume-based units (volumePer*, per*OfSalary)
  const isVolumeBased = useIsVolumeBased(rates);
  const quantityFieldName = quantityFieldOf(isVolumeBased);

  // Quantity column label adapts to unit type
  const enrolledLabel = getEnrolledLabel(
    isVolumeBased,
    rates[0]?.unit,
    coverageType,
  );

  const handleCommit =
    (parser: (val: string) => number, rateField: keyof Rate) =>
    (rate: Rate, value: string): void => {
      if (!axiosInstance || !rateSchedule?.id || !rate.id) return;

      const numericValue = parser(value);
      if (isNaN(numericValue) || numericValue === rate[rateField]) return;

      rateMutation.mutate(
        {
          scheduleId: rateSchedule.id,
          rateId: rate.id,
          benefitPlanId: plan.id,
          rate: { [rateField]: numericValue },
        },
        {
          onSuccess: () => onRateSaved(),
        },
      );
    };

  // Handle commit for numberOfLives (unit-based rates)
  const handleLivesCommit = handleCommit(parseIntBaseTen, "numberOfLives");

  // Handle commit for volume (volume-based rates)
  const handleVolumeCommit = handleCommit(parseFloat, "volume");

  // Unified quantity commit handler
  const handleQuantityCommit = isVolumeBased
    ? handleVolumeCommit
    : handleLivesCommit;

  // Get display value for quantity field
  const getQuantityValue = useCallback(
    (rate: Rate): string => {
      if (isVolumeBased) {
        return rate.volume != null ? String(rate.volume) : "";
      }
      return rate.numberOfLives != null ? String(rate.numberOfLives) : "";
    },
    [isVolumeBased],
  );

  // Handle commit for rate
  const handleRateCommit = handleCommit(parseFloat, "rate");

  // One copy column per tier; a tier's rates are matched by coverageTierCode.
  const copyColumns: RateCopyColumn[] = useMemo(
    () =>
      uniqueTiers.map((tier) => ({
        id: `tier:${tier}`,
        label: coverageTierToLabel(tier, uniqueTiers.length),
        matches: (rate: Rate) => rate.coverageTierCode === tier,
      })),
    [uniqueTiers],
  );

  const destinationPlans = useMemo(
    () => resolveDestinationPlans(plan, copyDestinationPlans, benefitPlanGroup),
    [plan, copyDestinationPlans, benefitPlanGroup],
  );

  const { renderCopyButton, copyDialog } = useAgeBandedRateCopy({
    plan,
    rates,
    rateSchedule,
    columns: copyColumns,
    rateDesign: RateDesignEnum.ageBandedTiered,
    destinationPlans,
    updateRateMutation,
    updateProposalRateMutation,
    onRateSaved,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!rateSchedule) {
    return (
      <Box sx={{ p: 1, fontSize: "13px", color: "text.secondary" }}>
        No rate schedule
      </Box>
    );
  }

  const numTiers = uniqueTiers.length;

  // Dynamic grid template: AgeBand column (flex) + 2 columns (Enrolled, Rate) per tier, plus
  // one read-only renewal column per tier when renewal is shown. The table scrolls within
  // the pinned benefit-plan cell (content-min tracks so it can overflow): a plan quoting
  // three or four tiers is wider than the Quotes grid lane, and without the scroll the
  // lane clips the trailing tiers out of view.
  const dataTracks = numTiers * 2 + (showRenewal ? numTiers : 0);
  const gridContainerSx = {
    display: "grid",
    gridTemplateColumns: `minmax(80px, 1fr) repeat(${dataTracks}, minmax(${
      showRenewal ? "76px" : "60px"
    }, 1fr))`,
    width: "100%",
    overflowX: "auto",
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        zIndex: 1,
      }}
    >
      {/* Header Row - Rate Design Label */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "36px",
          backgroundColor: AG_HEADER_BG_COLOR,
          borderBottom: AG_BORDER,
          px: 1,
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>
          Age Banded Tiered ({ageBandGroups.length} bands, {numTiers} tiers)
        </Box>
      </Box>

      <Box sx={gridContainerSx}>
        {/* Sub-header Row - Tier Labels */}
        <Box sx={{ ...headerCellSx }}>Age Band</Box>
        {uniqueTiers.map((tier, idx) => (
          <Box
            key={tier}
            sx={{
              ...headerCellSx,
              gridColumn: "span 2",
              justifyContent: "center",
              fontWeight: 600,
              // Last tier keeps its divider when a renewal group follows it.
              borderRight:
                idx === uniqueTiers.length - 1 && !showRenewal
                  ? "none"
                  : AG_BORDER,
            }}
          >
            {coverageTierToLabel(tier, numTiers)}
          </Box>
        ))}
        <RenewalGroupHeaderCell
          show={showRenewal}
          label={renewal.columnLabel}
          span={numTiers}
        />

        {/* Sub-sub-header Row - Column Labels (Enrolled / Rate for each tier) */}
        <Box sx={{ ...headerCellSx }}></Box>
        {uniqueTiers.map((tier, idx) => {
          const isLastTier = idx === uniqueTiers.length - 1;
          const tierLabel = coverageTierToLabel(tier, numTiers);
          return (
            <Fragment key={tier}>
              <Box sx={{ ...headerCellSx }}>{enrolledLabel}</Box>
              <Box
                sx={{
                  ...headerCellSx,
                  borderRight: isLastTier ? "none" : AG_BORDER,
                  justifyContent: "space-between",
                  gap: 0.5,
                }}
              >
                <span>Rate</span>
                {!readOnly && renderCopyButton(`tier:${tier}`, tierLabel)}
              </Box>
            </Fragment>
          );
        })}
        <RenewalSubHeaderCells
          show={showRenewal}
          labels={uniqueTiers.map((tier) =>
            coverageTierToLabel(tier, numTiers),
          )}
        />

        {/* Rate Rows - One per age band */}
        {ageBandGroups.map((group, rowIndex) => {
          const isLastRow =
            rowIndex === ageBandGroups.length - 1 &&
            companionRates.length === 0;
          const ageBandLabel = formatAgeBandLabel(group.lower, group.upper, {
            isHighestBand: rowIndex === ageBandGroups.length - 1,
            isCriticalIllness,
          });
          // 2 columns per tier (Enrolled, Rate)
          const columnsInRow = numTiers * 2;
          const baseCellIndex = rowIndex * columnsInRow;

          return (
            <Fragment key={`age-band-${group.lower}-${group.upper}`}>
              {/* Age Band Label */}
              <Box
                sx={{
                  ...headerCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {ageBandLabel}
              </Box>

              {/* Tier columns - Enrolled Lives and Rate for each tier */}
              {uniqueTiers.map((tier, tierIdx) => {
                const rate = group.ratesByTier.get(tier);
                const isLastTier = tierIdx === uniqueTiers.length - 1;
                // Each tier has 2 columns (Enrolled, Rate)
                const tierCellIndex = baseCellIndex + tierIdx * 2;

                return (
                  <Fragment key={tier}>
                    {/* Quantity Input */}
                    <RateReviewCell
                      planId={plan.id}
                      fieldPath={
                        rate &&
                        rateSchedule &&
                        buildRateFieldPath(
                          rateSchedule,
                          rate,
                          quantityFieldName,
                        )
                      }
                      sx={{
                        ...gridCellSx,
                        borderBottom: isLastRow ? "none" : AG_BORDER,
                      }}
                    >
                      {rate && (
                        <DebouncedNumericInput
                          serverValue={getQuantityValue(rate)}
                          onCommit={(value) =>
                            handleQuantityCommit(rate, value)
                          }
                          allowDecimals={isVolumeBased}
                          isCurrency={isVolumeBased}
                          sx={inputSx}
                          gridId={`age-banded-tiered-${plan.id}`}
                          cellIndex={tierCellIndex}
                          columnsInRow={columnsInRow}
                          readOnly={readOnly}
                          inputProps={{
                            "aria-label": `${ageBandLabel} ${coverageTierToLabel(tier, numTiers)} Enrolled Lives`,
                          }}
                        />
                      )}
                    </RateReviewCell>

                    {/* Rate Input */}
                    <RateReviewCell
                      planId={plan.id}
                      fieldPath={
                        rate &&
                        rateSchedule &&
                        buildRateFieldPath(rateSchedule, rate, "rate")
                      }
                      sx={{
                        ...gridCellSx,
                        borderRight: isLastTier ? "none" : AG_BORDER,
                        borderBottom: isLastRow ? "none" : AG_BORDER,
                      }}
                    >
                      {rate && (
                        <DebouncedNumericInput
                          serverValue={formatRateValue(
                            rate.rate,
                            rates[0]?.unit,
                          )}
                          onCommit={(value) => handleRateCommit(rate, value)}
                          allowDecimals
                          isCurrency
                          sx={inputSx}
                          gridId={`age-banded-tiered-${plan.id}`}
                          cellIndex={tierCellIndex + 1}
                          columnsInRow={columnsInRow}
                          readOnly={readOnly}
                          inputProps={{
                            "aria-label": `${ageBandLabel} ${coverageTierToLabel(tier, numTiers)} Rate`,
                          }}
                        />
                      )}
                    </RateReviewCell>
                  </Fragment>
                );
              })}
              <RenewalRowCells
                show={showRenewal}
                renewal={renewal}
                rowLabelPrefix={ageBandLabel}
                isLastRow={isLastRow}
                cells={uniqueTiers.map((tier) => ({
                  rate: group.ratesByTier.get(tier),
                  label: coverageTierToLabel(tier, numTiers),
                }))}
              />
            </Fragment>
          );
        })}

        {/* Companion Rate Section (dependent flat rates) */}
        {companionRates.length > 0 && companionRateSchedule && (
          <CompanionRatesSection
            companionRates={companionRates}
            companionRateSchedule={companionRateSchedule}
            planId={plan.id}
            isProposalRateSchedule={plan.rateScheduleSource === "proposal"}
            totalColumns={1 + dataTracks}
            renewalColumns={Number(showRenewal) * numTiers}
            onRateSaved={onRateSaved}
          />
        )}
      </Box>
      {copyDialog}
    </Box>
  );
};
